import React from 'react';
import { ArrowRightIcon, ChevronArrowRightIcon, CrossIconvector } from 'assets/icons';
import { CustomButton, CustomTextArea, If } from 'components';
import { ManualPaymentMethods } from 'containers/home/components/constants';
import { getAmountWithCurrency } from 'utils/amountUtils';
import { InvoiceByIdV2 } from '../invoices.model';
import { VoidFn } from 'types/baseTypes';
import { TipTimingEnum } from '../constants';

interface RecordPaymentDetailsProps {
  isRootState: boolean;
  handleSidePanelBack: () => void;
  invoiceData: InvoiceByIdV2;
  selectedMethodId: string;
  selectedAmount: number | null;
  referenceNote: string;
  handleReferenceNoteChange: (value: string) => void;
  handleEditAmount: () => void;
  recordPaymentHandler: () => void;
  markPaidLoading: boolean;
  handleOpenRecordTipModal: () => void;
  tipRecorded: number;
  handleMethodSelect: VoidFn;
}

const RecordPaymentDetails: React.FC<RecordPaymentDetailsProps> = ({
  isRootState,
  handleSidePanelBack,
  invoiceData,
  selectedMethodId,
  selectedAmount,
  referenceNote,
  handleReferenceNoteChange,
  handleEditAmount,
  recordPaymentHandler,
  markPaidLoading,
  handleOpenRecordTipModal,
  tipRecorded = 0,
  handleMethodSelect
}) => {
  const selectedMethod = ManualPaymentMethods.find(method => method.key === selectedMethodId);
  const currentPaymentAmount = selectedAmount || Number(invoiceData?.pendingAmount);
  const displayAmount = currentPaymentAmount + tipRecorded;

  const handleRecordTip = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleOpenRecordTipModal();
  };

  const shouldShowTipRecordButton = () => {
    if (invoiceData?.tip?.enabled && !tipRecorded) {
      if (invoiceData.tip.type === TipTimingEnum.FINAL_PAYMENT_ONLY) {
        return currentPaymentAmount === Number(invoiceData?.pendingAmount);
      }
      return true;
    }
    return false;
  };

  return (
    <>
      <div className="flex flex-row items-center px-4 pt-5 pb-3">
        <div
          id={`${isRootState ? 'record-payment-details-close-button' : 'record-payment-details-back-button'}`}
          onClick={handleSidePanelBack}
          className="cursor-pointer">
          {isRootState ? (
            <CrossIconvector className="shrink-0" />
          ) : (
            <ArrowRightIcon
              className="path-fill-current path h-5 w-5
              shrink-0 rotate-180 cursor-pointer text-heading"
            />
          )}
        </div>
      </div>
      <div className="flex h-[calc(100%-52px)] flex-col px-4 pb-4">
        <div className="mb-6 flex flex-col items-start">
          <div className="mb-2 text-17px font-bold text-primaryText">
            Record a cash payment of {getAmountWithCurrency(displayAmount)}
          </div>
          <div className={'text-px13 font-medium text-primaryText'}>
            Make sure the details for this payment are correct.
          </div>
        </div>

        <div className="mb-6">
          <div className="mb-2 text-sbase font-bold text-primaryText">Method</div>
          <div
            className="flex cursor-pointer items-center justify-between rounded-md bg-secondaryBtn py-3 pl-5 pr-4"
            onClick={handleMethodSelect}>
            <div className="text-sbase font-semibold text-primaryText">{selectedMethod?.value}</div>
            <ChevronArrowRightIcon className="path-fill-current shrink-0 text-gray-1" />
          </div>
        </div>

        <div className="mb-6">
          <div className="mb-2 text-sbase font-bold text-primaryText">Payment amount</div>
          <div
            className={`rounded-md bg-secondaryBtn py-3 pl-5 pr-4 transition-colors hover:bg-gray-100
              ${invoiceData?.partialPaymentEnabled ? 'cursor-pointer' : 'cursor-default'}
             `}
            onClick={invoiceData?.partialPaymentEnabled ? handleEditAmount : undefined}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sbase font-semibold text-primaryText">{getAmountWithCurrency(displayAmount)}</div>
              </div>
              <div className="flex items-center">
                <div onClick={handleRecordTip} className="cursor-pointer text-sbase font-semibold text-primaryBtn">
                  <If condition={Boolean(tipRecorded)}>
                    <div className="text-px13">
                      {getAmountWithCurrency(currentPaymentAmount || 0)} + {getAmountWithCurrency(tipRecorded)} Tip
                    </div>
                  </If>
                  <If condition={shouldShowTipRecordButton()}>
                    <div>+Record a tip</div>
                  </If>
                </div>
                <ChevronArrowRightIcon className="path-fill-current path text-gray-1" />
              </div>
            </div>
          </div>
        </div>

        <div className="mb-2">
          <div className="mb-2 text-sbase font-bold text-primaryText">Reference or notes</div>
          <div className="mb-1 text-px13 font-medium text-accentText">Visible to customer on receipt</div>
          <CustomTextArea
            resizeTextArea={true}
            id="payment-reference-notes"
            maxLength={1000}
            name="paymentNotes"
            placeholder="Enter amount / date received or any other relevant information"
            value={referenceNote}
            onChange={handleReferenceNoteChange}
            className="min-h-[70px] py-[15px] text-[15px] font-normal
              leading-5 text-primaryText outline-none placeholder:text-[15px]"
            showRemainingChar={true}
          />
        </div>

        <div className="mt-auto space-y-4">
          <CustomButton
            id="record-payment-confirm-button"
            className="path-stroke-current min-h-12
              w-full"
            onClick={recordPaymentHandler}
            childrenContainerStyles="text-17px font-normal"
            disabled={!invoiceData.isModifiable || !selectedMethodId || displayAmount <= 0}
            isLoading={markPaidLoading}>
            Record {getAmountWithCurrency(displayAmount)} as paid
          </CustomButton>
        </div>
      </div>
    </>
  );
};

export default RecordPaymentDetails;
